import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllTimetables,
  getTimetableById,
  createTimetable,
  updateTimetable,
  deleteTimetable
} from '../services/timetableService';
import Notification from '../components/Common/Notification';

export const TimetableContext = createContext();

export const TimetableProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [timetables, setTimetables] = useState([]);
  const [selectedTimetable, setSelectedTimetable] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllTimetables = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllTimetables(authHeaders);
      setTimetables(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching timetables.';
      showNotification(errorMessage, true);
    }
  };

  const fetchTimetableById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getTimetableById(id, authHeaders);
      setSelectedTimetable(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching timetable with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addTimetable = async (timetableData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newTimetable = await createTimetable(timetableData, authHeaders);
      setTimetables(prevTimetables => [...prevTimetables, newTimetable]);
      showNotification('Timetable added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding timetable.';
      showNotification(errorMessage, true);
    }
  };

  const editTimetable = async (id, timetableData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedTimetable = await updateTimetable(id, timetableData, authHeaders);
      setTimetables(prevTimetables =>
        prevTimetables.map(t => (t.id === id ? updatedTimetable : t))
      );
      showNotification('Timetable updated successfully!', false);
      return updatedTimetable;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating timetable.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeTimetable = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteTimetable(id, authHeaders);
      setTimetables(prevTimetables => prevTimetables.filter(t => t.id !== id));
      showNotification('Timetable removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing timetable.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllTimetables();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <TimetableContext.Provider
      value={{
        timetables,
        selectedTimetable,
        fetchAllTimetables,
        fetchTimetableById,
        addTimetable,
        editTimetable,
        removeTimetable,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </TimetableContext.Provider>
  );
};
